import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Sun, Moon, LogOut, User as UserIcon, ChevronDown, X, ActivitySquare, Clock, Sparkles, BookOpen, PlayCircle, Menu } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";
import { useTheme } from "@/lib/theme-context";
import api from "@/lib/api";
import CommandPalette, { useCommandPalette } from "@/components/CommandPalette";
import HelpModeToggle from "@/components/HelpModeToggle";
import ProfileCompletionChip from "@/components/layout/ProfileCompletionChip";
import TrialDaysChip from "@/components/layout/TrialDaysChip";
import GettingStartedVideoDialog from "@/components/layout/GettingStartedVideoDialog";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

const ROLE_LABELS = {
    super_admin: "SUPER ADMIN",
    org_admin: "ORG ADMIN",
    entity_admin: "ADMIN",
    entity_viewer: "ANALYST",
    viewer: "VIEWER",
};

export default function TopBar({ onMobileMenuClick, impersonating = false }) {
    const { user, logout, refreshMe, setUser } = useAuth();
    const { theme, toggle } = useTheme();
    const [profileOpen, setProfileOpen] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const { open: cmdkOpen, setOpen: setCmdkOpen } = useCommandPalette();

    // iter-120 (Feb 2026) — "Replay tours" handler. Clears every
    // dismissable onboarding flag stored under `user.ui_prefs` so the
    // user can re-encounter the tour cards as they navigate. The
    // backend allow-list (`_UI_PREF_KEYS`) defines which flags are
    // tour-related; we clear each one with a `null` value to $unset it.
    const replayTours = async () => {
        try {
            // Currently the only one-time tour. Add more keys here as
            // we ship additional onboarding cards.
            const r = await api.patch("/auth/me/ui-prefs", {
                seen_schema_mapping_onboarding: null,
            });
            const nextPrefs = r.data?.ui_prefs || {};
            setUser((u) => (u ? { ...u, ui_prefs: nextPrefs } : u));
            try {
                const raw = localStorage.getItem("analyzio_user");
                if (raw) {
                    const cached = JSON.parse(raw);
                    cached.ui_prefs = nextPrefs;
                    localStorage.setItem("analyzio_user", JSON.stringify(cached));
                }
            } catch { /* harmless */ }
            toast.success("Tours reset", {
                description: "Open the Schema mapping tab to see the onboarding tour again.",
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not reset tours");
        }
    };

    const initials = (user?.name || user?.email || "?")
        .split(/\s+/)
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <header
            className={`h-[60px] border-b border-border bg-background sticky ${impersonating ? "top-[35px]" : "top-0"} z-20 flex items-center gap-2 sm:gap-4 px-3 sm:px-4 md:px-6`}
            data-testid="app-topbar"
        >
            {/* iter-121.78 — mobile-only hamburger trigger; opens the
                sidebar Sheet from AppShell. Hidden ≥ md where the
                persistent sidebar is visible. */}
            {onMobileMenuClick && (
                <button
                    type="button"
                    onClick={onMobileMenuClick}
                    className="md:hidden h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary rounded-sm transition-colors shrink-0"
                    data-testid="topbar-mobile-menu"
                    aria-label="Open navigation menu"
                >
                    <Menu className="h-5 w-5" />
                </button>
            )}
            <div className="flex-1 max-w-xl mx-auto relative min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <button
                    type="button"
                    onClick={() => setCmdkOpen(true)}
                    className="w-full h-9 pl-9 pr-3 sm:pr-16 bg-secondary/40 border border-border rounded-sm text-sm text-left text-muted-foreground hover:bg-background hover:border-primary/50 transition-colors truncate"
                    data-testid="global-search-trigger"
                >
                    <span className="hidden sm:inline">Search dashboards, datasets, KPIs, settings…</span>
                    <span className="sm:hidden">Search…</span>
                </button>
                <kbd className="hidden sm:block absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-muted-foreground bg-background border border-border px-1.5 py-0.5 rounded-sm pointer-events-none">
                    ⌘K
                </kbd>
            </div>

            <div className="flex items-center gap-1 shrink-0">
                {/* iter-121.78 — Hide the chips on very narrow screens
                    so the topbar fits hamburger + search + avatar
                    inside 360px. The chips reappear at sm:640px. */}
                <div className="hidden sm:flex items-center gap-1">
                    <TrialDaysChip />
                    <ProfileCompletionChip />
                    <LlmSpendChip />
                </div>
                {/*Helper icon is removed by Vishal*/}
                {/* <HelpModeToggle />
                <button
                    onClick={toggle}
                    className="h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary rounded-sm transition-colors"
                    data-testid="theme-toggle-button"
                    aria-label="Toggle theme"
                >
                    {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button> */}

                <NotificationsPopover />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="h-9 pl-1 pr-3 flex items-center gap-2 hover:bg-secondary rounded-sm transition-colors"
                            data-testid="user-menu-button"
                            data-testid-alias="topbar-avatar"
                            id="topbar-avatar"
                            aria-label="Open user menu"
                        >
                            <div className="w-7 h-7 bg-foreground text-background font-mono text-[11px] font-semibold flex items-center justify-center rounded-sm">
                                {initials}
                            </div>
                            <div className="hidden sm:flex flex-col items-start leading-none gap-0.5">
                                <span className="text-sm">{user?.name?.split(" ")[0]}</span>
                                {user?.role && (
                                    <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground"
                                          data-testid="topbar-role-chip"
                                          title={`Signed in as ${ROLE_LABELS[user.role] || user.role}`}>
                                        {ROLE_LABELS[user.role] || user.role}
                                    </span>
                                )}
                            </div>
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuLabel className="font-normal">
                            <div className="text-sm font-medium">{user?.name}</div>
                            <div className="text-xs text-muted-foreground font-mono mt-0.5">{user?.email}</div>
                            <Badge variant="outline" className="mt-2 font-mono text-[10px]" data-testid="user-role-badge">
                                {ROLE_LABELS[user?.role] || user?.role}
                            </Badge>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setProfileOpen(true); }} data-testid="menu-profile">
                            <UserIcon className="h-4 w-4 mr-2" /> Profile
                        </DropdownMenuItem>
                        {/* iter-121 — Pinned Getting Started video. URL comes from
                            `settings.platform.getting_started_video_url` so SA
                            can swap the video without a deploy. */}
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setVideoOpen(true); }} data-testid="menu-getting-started">
                            <PlayCircle className="h-4 w-4 mr-2 text-primary" /> Getting started video
                        </DropdownMenuItem>
                        {/* iter-120 (Feb 2026) — re-show every dismissed
                            one-time onboarding card. Useful when a teammate
                            wants to walk through the flows again. */}
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); replayTours(); }} data-testid="menu-replay-tours">
                            <BookOpen className="h-4 w-4 mr-2" /> Replay tours
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-primary">
                            <LogOut className="h-4 w-4 mr-2" /> Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} refreshMe={refreshMe} />
            <GettingStartedVideoDialog open={videoOpen} onOpenChange={setVideoOpen} />
            <CommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} />
        </header>
    );
}

// ----------------------------------------------------------------
// Notifications popover — pulls from activity log
// ----------------------------------------------------------------
function NotificationsPopover() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/activity?limit=12");
            const rows = Array.isArray(r.data) ? r.data : (r.data.items || []);
            setItems(rows);
        } catch {
            setItems([]);
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        if (open && !loaded) load();
    }, [open, loaded]);

    const unread = items.length > 0 && !loaded;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    className="h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary rounded-sm transition-colors relative"
                    data-testid="notifications-button"
                    aria-label="Notifications"
                >
                    <Bell className="h-4 w-4" />
                    {unread && <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-primary rounded-full" />}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[380px] p-0" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <div className="flex items-center gap-2">
                        <ActivitySquare className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium">Notifications</span>
                        {items.length > 0 && <Badge variant="outline" className="font-mono text-[10px]">{items.length}</Badge>}
                    </div>
                    <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="notifications-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                    {!loaded ? (
                        <div className="py-10 text-center text-xs text-muted-foreground">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="py-10 text-center text-xs text-muted-foreground" data-testid="notifications-empty">
                            You're all caught up.
                        </div>
                    ) : items.map((it) => (
                        <NotificationRow key={it.id} item={it} />
                    ))}
                </div>
                <div className="px-4 py-2 border-t border-border bg-secondary/30 text-[11px] text-muted-foreground font-mono">
                    Powered by your tenant's Activity Log
                </div>
            </PopoverContent>
        </Popover>
    );
}

function NotificationRow({ item }) {
    const when = item.timestamp || item.created_at;
    const dt = when ? new Date(when) : null;
    const verb = (item.action || "event").replace(/_/g, " ");
    return (
        <div className="px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
            <div className="flex items-start gap-3">
                <div className="w-7 h-7 shrink-0 rounded-sm bg-foreground/5 border border-border flex items-center justify-center">
                    <ActivitySquare className="h-3 w-3 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm leading-snug">
                        <span className="font-medium">{item.user_email || item.actor_email || "System"}</span>
                        <span className="text-muted-foreground"> · {verb}</span>
                        {item.target_type && <span className="text-muted-foreground"> · {item.target_type}</span>}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        {dt ? dt.toLocaleString() : "—"}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------
// Credit-balance chip — shows the APPLICABLE organization's current Sense AI
// credit balance (used / allowance + remaining) using the SAME source as
// Billing, Reports → Cost & Usage and the sidebar badge
// (/billing/usage-summary → ai_credits, backed by ai_credits_state over the
// current billing cycle). No independent/second credit calculation.
// ----------------------------------------------------------------
function fmtCreditsCompact(n) {
    if (n == null) return "—";
    const v = Number(n);
    if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, "") + "K";
    return v.toLocaleString();
}

function LlmSpendChip() {
    const { user } = useAuth();
    const { canAccess } = useSettings();
    const [c, setC] = useState(null);   // ai_credits block
    const navigate = useNavigate();

    const load = async () => {
        try {
            const r = await api.get("/billing/usage-summary");
            setC(r.data?.ai_credits || null);
        } catch { /* hide silently */ }
    };

    useEffect(() => {
        if (!user) return;
        load();
        const interval = setInterval(load, 60_000);
        const refresh = () => load();
        window.addEventListener("llm-spend-refresh", refresh);
        window.addEventListener("billing:credits-updated", refresh);
        return () => {
            clearInterval(interval);
            window.removeEventListener("llm-spend-refresh", refresh);
            window.removeEventListener("billing:credits-updated", refresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    if (!c) return null;
    // Credit detail lives in Reports → Cost & Usage (analyze) / Billing (manage).
    const reportsAllowed = canAccess ? canAccess("reports") : false;
    const go = () => { if (reportsAllowed) navigate("/reports"); };
    const unlimited = c.unlimited || c.included == null;
    const titleStr = unlimited
        ? "AI credits — unlimited plan allowance"
        : `AI credits: ${Number(c.used || 0).toLocaleString()} / ${Number(c.included || 0).toLocaleString()} used · ${Number(c.remaining || 0).toLocaleString()} remaining${reportsAllowed ? " — click for Cost & Usage" : ""}`;
    return (
        <button
            onClick={go}
            className={`hidden md:inline-flex items-center gap-1.5 h-9 px-2.5 mr-1 border border-border rounded-sm transition-colors group ${reportsAllowed ? "hover:border-primary/50 cursor-pointer" : "cursor-default"}`}
            data-testid="topbar-llm-spend-chip"
            title={titleStr}>
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Credits</span>
            {unlimited ? (
                <span className="text-xs font-medium tabular-nums" data-testid="topbar-llm-spend-amount">Unlimited</span>
            ) : (
                <>
                    <span className="text-xs font-medium tabular-nums" data-testid="topbar-llm-spend-amount">
                        {fmtCreditsCompact(c.remaining)} left
                    </span>
                    <span className="hidden lg:inline text-[10px] font-mono text-muted-foreground">
                        · {fmtCreditsCompact(c.used)}/{fmtCreditsCompact(c.included)}
                    </span>
                </>
            )}
        </button>
    );
}

// ----------------------------------------------------------------
// Profile dialog
// ----------------------------------------------------------------
function ProfileDialog({ open, onOpenChange, user, refreshMe }) {
    const [name, setName] = useState(user?.name || "");
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [busy, setBusy] = useState(false);
    const [pwdBusy, setPwdBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setName(user?.name || "");
            setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
        }
    }, [open, user]);

    if (!user) return null;
    const initials = (user.name || user.email).split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    const saveProfile = async () => {
        if (!name.trim()) { toast.error("Name cannot be empty"); return; }
        setBusy(true);
        try {
            await api.patch(`/users/${user.id}`, { name: name.trim() });
            if (refreshMe) await refreshMe();
            toast.success("Profile updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    const changePassword = async () => {
        if (newPwd.length < 8) { toast.error("New password must be at least 8 characters"); return; }
        if (newPwd !== confirmPwd) { toast.error("New password and confirmation don't match"); return; }
        setPwdBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
            toast.success("Password updated");
            setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Password change failed");
        } finally {
            setPwdBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Your profile</DialogTitle></DialogHeader>
                <div className="space-y-5">
                    <div className="flex items-center gap-4 p-4 border border-border rounded-sm bg-secondary/30">
                        <div className="w-12 h-12 bg-foreground text-background font-mono text-base font-semibold flex items-center justify-center rounded-sm">
                            {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate" data-testid="profile-name">{user.name}</div>
                            <div className="text-xs font-mono text-muted-foreground truncate">{user.email}</div>
                            <Badge variant="outline" className="mt-1.5 font-mono text-[10px]">{ROLE_LABELS[user.role] || user.role}</Badge>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="eyebrow">Account details</div>
                        <div>
                            <label className="eyebrow mb-1.5 block">Full name</label>
                            <input
                                value={name} onChange={(e) => setName(e.target.value)}
                                className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm"
                                data-testid="profile-name-input"
                            />
                        </div>
                        <div>
                            <label className="eyebrow mb-1.5 block">Email</label>
                            <div className="h-10 px-3 flex items-center bg-secondary/40 border border-border rounded-sm text-xs font-mono text-muted-foreground">{user.email}</div>
                        </div>
                        <Button onClick={saveProfile} disabled={busy} className="rounded-sm" data-testid="profile-save">
                            {busy ? "Saving…" : "Save changes"}
                        </Button>
                    </div>

                    <div className="space-y-3 pt-5 border-t border-border">
                        <div className="eyebrow">Change password</div>
                        <div className="grid grid-cols-1 gap-3">
                            <input type="password" placeholder="Current password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
                                   className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm"
                                   data-testid="profile-current-password" />
                            <input type="password" placeholder="New password (min 8 chars)" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                                   className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm"
                                   data-testid="profile-new-password" />
                            <input type="password" placeholder="Confirm new password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                                   className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm"
                                   data-testid="profile-confirm-password" />
                        </div>
                        <Button onClick={changePassword} disabled={pwdBusy || !currentPwd || !newPwd} variant="outline" className="rounded-sm" data-testid="profile-change-password">
                            {pwdBusy ? "Updating…" : "Update password"}
                        </Button>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="profile-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
